'use client';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@features/ui/components/dropdown-menu';

import {cn} from '@features/ui/utils';
import {useExportButtonDisabled, useExportEnabled} from './useExportToFigma';
import ChangeVariantIcon from '@/icons/change-variant.svg';
import DropdownArrow from '@/icons/dropdown-arrow-new.svg';
import {useCreateVariant} from '../hooks/useVariateScreen';
import {
  createVariantPrompts,
  type CreateVariantAction,
} from '@features/ai/prompt/common/variants';
import {useHasActiveStructure} from './canvas-feedback-buttons';
import {memo, useContext} from 'react';
import {FlowContext} from '@features/contexts/FlowContext';

const createVariantOptionGroups: CreateVariantAction[][] = [
  ['try-layout', 'change-colors'],
  ['simplify', 'add-content'],
  ['make-better', 'fix-alignment', 'regenerate'],
];

type CanvasCreateVariantDropdownProps = {
  screenId: string;
};

export const CanvaCreateVariantDropdown = memo(
  ({screenId}: CanvasCreateVariantDropdownProps) => {
    const {flow} = useContext(FlowContext);
    const isComponents = flow.genType === 'components';
    const exportEnabled = useExportEnabled();
    const isDisabled = useExportButtonDisabled();
    const hasActiveStructure = useHasActiveStructure(screenId);
    const {createVariant: variateScreen, isPending} = useCreateVariant();

    const handleAction = (action: CreateVariantAction) => {
      const {prompt} = createVariantPrompts[action];
      void variateScreen(screenId, prompt, action);
    };

    // Show for HTML/CSS flows (gated by export) and component flows
    if (!exportEnabled && !isComponents) return null;

    const buttonDisabled =
      (isComponents ? false : isDisabled) || isPending || !hasActiveStructure;

    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className={cn(
              'flex h-full items-center gap-0.5 rounded-md pl-1 pr-1.5 text-sm font-semibold text-white transition-colors',
              !buttonDisabled && 'hover:bg-white/20',
              buttonDisabled && 'opacity-50',
            )}
            disabled={buttonDisabled}
          >
            <ChangeVariantIcon />
            Variant
            <DropdownArrow className='-ml-0.5 size-4 text-white/60' />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align='center' sideOffset={8} className='w-52'>
          {createVariantOptionGroups.map((group, groupIndex) => (
            <div key={groupIndex}>
              {groupIndex > 0 && <DropdownMenuSeparator />}
              {group.map((action) => (
                <DropdownMenuItem
                  key={action}
                  onClick={() => handleAction(action)}
                >
                  {createVariantPrompts[action].label}
                </DropdownMenuItem>
              ))}
            </div>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  },
);
CanvaCreateVariantDropdown.displayName = 'CanvaCreateVariantDropdown';
